import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { useLocale, useTranslations } from "use-intl";
import z from "zod";

import { AuthAlternativeMethods } from "@/components/auth-alternative-methods";
import { useAppForm } from "@/components/forms/app-form";
import { useCurrency } from "@/hooks/use-currency";
import { useSession } from "@/hooks/use-session";
import { trackAccountCreation, trackSignupStarted } from "@/lib/analytics";
import { authClient } from "@/lib/auth-client";
import { buildOnboardingHrefWithRedirect, getRedirectAfterLogin } from "@/lib/redirect-after-login";
import { invalidateSession } from "@/utils/session";

import Loader from "./loader";
import { PasswordStrength } from "./password-strength";

interface SignUpFormProps {
  onSuccess?: () => void;
  redirectAfterLogin?: string;
  showHeader?: boolean;
}

export default function SignUpForm({
  onSuccess,
  redirectAfterLogin,
  showHeader = true,
}: SignUpFormProps) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { isPending } = useSession();
  const t = useTranslations("globals.auth");
  const locale = useLocale();
  const { currency } = useCurrency();
  const [whatsappExpanded, setWhatsAppExpanded] = useState(false);
  const signUpSchema = z
    .object({
      name: z.string().trim().min(2, t("nameMinLength")),
      email: z.email(t("invalidEmail")),
      password: z.string().min(8, t("passwordMinLength")),
      confirmPassword: z.string(),
    })
    .refine((value) => value.password === value.confirmPassword, {
      message: t("passwordsDoNotMatch"),
      path: ["confirmPassword"],
    });

  const form = useAppForm({
    defaultValues: {
      email: "",
      password: "",
      confirmPassword: "",
      name: "",
    },
    onSubmit: async ({ value }) => {
      const input = signUpSchema.parse(value);
      const finalRedirect = getRedirectAfterLogin(redirectAfterLogin);
      const onboardingHref = buildOnboardingHrefWithRedirect(finalRedirect);

      trackSignupStarted("email", "method");

      const result = await authClient.signUp.email({
        email: input.email,
        password: input.password,
        name: input.name,
        language: locale,
        currency,
        callbackURL: onboardingHref,
      });

      if (result.error) {
        toast.error(result.error.message || result.error.statusText);
        return;
      }

      trackAccountCreation("email", result.data.user.id);
      await invalidateSession(queryClient);
      await router.invalidate();
      await router.navigate({ href: onboardingHref });
      onSuccess?.();
      toast.success(t("signUpSuccess"));
    },
    validators: {
      onSubmit: signUpSchema,
    },
  });

  if (isPending) {
    return <Loader />;
  }

  return (
    <div className="space-y-6">
      {showHeader && (
        <div className="space-y-2">
          <h1 className="text-2xl font-bold tracking-tight">{t("createAccount")}</h1>
          <p className="hidden text-sm text-muted-foreground lg:block">
            {t("createAccountDescription")}
          </p>
        </div>
      )}

      <AuthAlternativeMethods
        mode="signup"
        redirectAfterLogin={redirectAfterLogin}
        onWhatsAppExpandedChange={setWhatsAppExpanded}
      />

      {!whatsappExpanded && (
        <form.AppForm>
          <form.Form className="space-y-4">
            <form.AppField name="name">
              {(field) => <field.TextField label={t("name")} />}
            </form.AppField>

            <form.AppField name="email">
              {(field) => <field.TextField label={t("email")} type="email" />}
            </form.AppField>

            <form.AppField name="password">
              {(field) => (
                <div className="space-y-1.5">
                  <field.PasswordField label={t("password")} />
                  <PasswordStrength password={field.state.value} />
                </div>
              )}
            </form.AppField>

            <form.AppField
              name="confirmPassword"
              validators={{
                onChangeListenTo: ["password"],
                onChange: ({ value }) => {
                  if (value && value !== form.getFieldValue("password")) {
                    return t("passwordsDoNotMatch");
                  }
                  return undefined;
                },
              }}
            >
              {(field) => <field.PasswordField label={t("confirmPassword")} />}
            </form.AppField>

            <form.SubmitButton className="w-full" size="lg" submittingLabel={t("submitting")}>
              {t("signUp")}
            </form.SubmitButton>
          </form.Form>
        </form.AppForm>
      )}
    </div>
  );
}
